import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 25000-25999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const filesErrors = {
  // 25xxx is the fileUpload module's series, opened by its SOMETHING_WENT_WRONG (9000) /
  // RESOURCE_NOT_FOUND (9003) split. Eight sites stay generic: three literally say
  // "Something went wrong" and five forward a caught error or a child service's own message.
  //
  // Shared across modules on purpose — takeoffs sends it too. Deliberately NOT
  // ESTIMATE_FILE_VERSION_NOT_FOUND (1122), which is scoped to an estimate's own file versions even
  // though it happens to carry the same sentence. One of the five sites writes the sentence with a
  // trailing period; punctuation is non-material, so they share this code.
  // 25001-25024 — the fileUpload module's split.
  FILE_VERSION_NOT_FOUND: {
    errorCode: 25001,
    errorMessage: t("File version not found"),
    isDynamic: false,
  },
  FILES_CANNOT_BE_MERGED_MATCHING_CONSTITUENT: {
    errorCode: 25002,
    errorMessage: t("Files cannot be merged. Matching constituent file found."),
    isDynamic: false,
  },
  FILE_ANNOTATIONS_SAVE_FAILED: {
    errorCode: 25003,
    errorMessage: t("The annotations could not be saved."),
    isDynamic: false,
  },
  FILE_NO_REVISION_SERIES_CONFIGURED: {
    errorCode: 25004,
    errorMessage: t("No revision series configured"),
    isDynamic: false,
  },
  FILE_NEXT_REVISION_UNDETERMINED: {
    errorCode: 25005,
    errorMessage: t("Could not determine the next revision"),
    isDynamic: false,
  },
  FILE_VERSION_NOT_A_REVISION: {
    errorCode: 25006,
    errorMessage: t("This version is not marked as a revision"),
    isDynamic: false,
  },
  FILE_NO_NAMING_CONVENTION_SOURCE: {
    errorCode: 25007,
    errorMessage: t("No folder or batch was supplied, so there is no naming convention to read."),
    isDynamic: false,
  },
  FOLDER_NOT_FOUND: {
    errorCode: 25008,
    errorMessage: t("Folder not found."),
    isDynamic: false,
  },
  // Two different points in container creation, each naming which write did not land.
  FILE_CONTAINER_NOT_CREATED_NO_RECORD: {
    errorCode: 25009,
    errorMessage: t("The container was not created: no file record was written."),
    isDynamic: false,
  },
  FILE_CONTAINER_NOT_CREATED_NOT_INSERTED: {
    errorCode: 25010,
    errorMessage: t("The container was not created: the file record was not inserted."),
    isDynamic: false,
  },
  FILE_UPLOAD_FAILED: {
    errorCode: 25011,
    errorMessage: t("File upload failed"),
    isDynamic: false,
  },
  // The stream-upload catch arm's fallback when the thrown value carries no message. Its sentence
  // is staging's "Upload failed" verbatim, which is a different string from 25011's — not a
  // punctuation variant — so it gets its own code rather than reusing FILE_UPLOAD_FAILED.
  FILE_UPLOAD_FAILED_PLAIN: {
    errorCode: 25033,
    errorMessage: t("Upload failed"),
    isDynamic: false,
  },
  FILE_PROPERTIES_NONE_TO_UPDATE: {
    errorCode: 25012,
    errorMessage: t("No properties were sent to update."),
    isDynamic: false,
  },
  FILE_PLUGIN_PROPERTIES_UPDATE_FAILED: {
    errorCode: 25013,
    errorMessage: t("Failed to update plugin properties."),
    isDynamic: false,
  },
  FILE_SIZE_EXCEEDS_LIMIT: {
    errorCode: 25014,
    errorMessage: t("File size exceeds maximum allowed limit"),
    isDynamic: false,
  },
  FILE_NO_VALID_FILE: {
    errorCode: 25015,
    errorMessage: t("No valid file found"),
    isDynamic: false,
  },
  FILE_UPLOAD_STREAM_ERROR: {
    errorCode: 25016,
    errorMessage: t("Upload stream error"),
    isDynamic: false,
  },
  // 25017-25024 are the eight revision-rule refusals, one code per rule rather than a single umbrella
  // code that would lose which rule was broken. These eight NAMES are also the members of
  // `RevisionRuleViolation` (helpers/revisionPattern) — `validateRevisionAgainstRules` returns one of
  // them, so a caller reaches the code with `errorCodes[violation]` and the sentence through the
  // mapper behind it. Renaming one of these breaks that file's `satisfies` guard at compile time.
  // 25017-25024 are the eight revision-rule refusals. These entries are now the ONLY copy of those
  // sentences — the server-side and Excel-import message maps that used to duplicate them were
  // removed, and both sides resolve through this mapper instead.
  REVISION_NOT_IN_SERIES: {
    errorCode: 25017,
    errorMessage: t("Revision is not part of the assigned revision series"),
    isDynamic: false,
  },
  REVISION_MUST_BE_INITIAL: {
    errorCode: 25018,
    errorMessage: t("First revision must start at the first value of the series"),
    isDynamic: false,
  },
  REVISION_SEQUENTIAL_VIOLATION: {
    errorCode: 25019,
    errorMessage: t("Revision must be the same as or next after the previous revision"),
    isDynamic: false,
  },
  REVISION_MUST_ADVANCE: {
    errorCode: 25020,
    errorMessage: t("Revision must be later in the series than the previous revision"),
    isDynamic: false,
  },
  REVISION_BACKWARDS_NOT_ALLOWED: {
    errorCode: 25021,
    errorMessage: t("Upload of earlier revision is not allowed"),
    isDynamic: false,
  },
  REVISION_MUST_PRECEDE_NEXT: {
    errorCode: 25022,
    errorMessage: t("Revision must be earlier in the series than the following revision"),
    isDynamic: false,
  },
  REVISION_REPEATED: {
    errorCode: 25023,
    errorMessage: t("This revision is already assigned to another version of this file"),
    isDynamic: false,
  },
  REVISION_REQUIRED: {
    errorCode: 25024,
    errorMessage: t("Metadata is not available in the Document Register, file cannot be uploaded"),
    isDynamic: false,
  },
  // Shared file-domain code: fileSystemServer (3 sites) and fileOperations (2) all ship exactly
  // "File not found". Deliberately NOT PROJECT_FILE_VERSION_NOT_FOUND (20017), which carries the
  // same sentence but names a project's file VERSION.
  // 25025 — shared across fileSystemServer and fileOperations.
  FILE_NOT_FOUND: {
    errorCode: 25025,
    errorMessage: t("File not found"),
    isDynamic: false,
  },
  // Off INSUFFICIENT_PERMISSION (7012): a missing folder argument is a validation failure.
  FILE_NO_FOLDER_FOR_NAMING_CONVENTION: {
    errorCode: 25026,
    errorMessage: t("No folder was specified, so there is no naming convention to set."),
    isDynamic: false,
  },
  FILE_MERGE_NAME_EXISTS: {
    errorCode: 25027,
    errorMessage: t("Merge File Name already exists."),
    isDynamic: false,
  },
  FILE_ANNOTATIONS_FILE_MISSING: {
    errorCode: 25028,
    errorMessage: t("Annotations file is missing from the request"),
    isDynamic: false,
  },
  FILE_CONTAINER_SPACE_REQUIRED: {
    errorCode: 25029,
    errorMessage: t("No Space was supplied, so there is nowhere to create the container."),
    isDynamic: false,
  },
  FILE_CONTAINER_FOLDER_AND_SPACE_REQUIRED: {
    errorCode: 25030,
    errorMessage: t("A destination folder and Space are both required to create the container."),
    isDynamic: false,
  },
  FILE_VERSION_NOT_FOUND_REQUESTED: {
    errorCode: 25031,
    errorMessage: t("The requested file version was not found."),
    isDynamic: false,
  },
  FILE_RESOURCE_TYPE_UNSUPPORTED: {
    errorCode: 25032,
    errorMessage: t('Unsupported resourceType "{{resourceType}}" — expected {{fileType}} or {{embedType}}.'),
    isDynamic: true,
    dynamicParams: ["resourceType", "fileType", "embedType"],
  },
  // 25034 — staging's bulk-upload pairing guard. multer can silently drop a part, and the files are
  // paired BY INDEX, so a length mismatch would write each file onto the WRONG version row. It
  // arrived sending only an inline `errorMessage` and no code at all, which is also what broke the
  // controller's union narrowing (the refusal arm carried neither `result` nor `errorCode`).
  FILE_BULK_UPLOAD_COUNT_MISMATCH: {
    errorCode: 25034,
    errorMessage: t(
      "This upload carried {{uploaded}} file(s) for {{selected}} selected file(s). Nothing was saved — please try again.",
    ),
    isDynamic: true,
    dynamicParams: ["uploaded", "selected"],
  },
  // 25035-25040 — the file-type / file-content upload gate that arrived from staging
  // (fileTypeGate.ts, fileContentGate.ts, streamUpload, filesystem-server). Every refusal there
  // carried its sentence as an inline `errorMessage` on a `FileTypeNotAllowedError`; the class now
  // carries a code and params instead. Sentences are staging's own, verbatim.
  FILE_UPLOAD_PORTAL_UNRESOLVED: {
    errorCode: 25035,
    errorMessage: t("This upload could not be matched to a portal."),
    isDynamic: false,
  },
  FILE_TYPE_NOT_ACCEPTED_BY_PORTAL: {
    errorCode: 25036,
    errorMessage: t("{{fileName}} is not a file type this portal accepts."),
    isDynamic: true,
    dynamicParams: ["fileName"],
  },
  FILE_CONTENT_EXECUTABLE_NAMED: {
    errorCode: 25037,
    errorMessage: t("{{fileName}} holds executable content, which cannot be uploaded."),
    isDynamic: true,
    dynamicParams: ["fileName"],
  },
  FILE_CONTENT_EXECUTABLE: {
    errorCode: 25038,
    errorMessage: t("Executable file content is not allowed"),
    isDynamic: false,
  },
  FILE_CONTENT_EXTENSION_MISMATCH: {
    errorCode: 25039,
    errorMessage: t("{{detectedLabel}} content does not match the {{claimedExtension}} extension"),
    isDynamic: true,
    dynamicParams: ["detectedLabel", "claimedExtension"],
  },
  FILE_EXECUTABLE_SCRIPT_BLOCKED: {
    errorCode: 25040,
    errorMessage: t("Executable and script files cannot be uploaded"),
    isDynamic: false,
  },
  FILE_ANSWERS_INVALID_FOR_QUESTION: {
    errorCode: 25041,
    errorMessage: t("These answers are not valid for their question: {{questions}}."),
    isDynamic: true,
    dynamicParams: ["questions"],
  },
  // An office engine (Document / Spreadsheet) on a non-office extension — refused by every engine
  // writer: saveEngineType and the portal file-type create/edit.
  FILE_ENGINE_NOT_ALLOWED_FOR_TYPE: {
    errorCode: 25042,
    errorMessage: t("The {{engine}} engine cannot open {{extension}} files."),
    isDynamic: true,
    dynamicParams: ["engine", "extension"],
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
